/**
 * AggregatrDatasetTool - Universal Dataset Management Interface
 * 
 * Main component for managing datasets across OpCentral applications.
 * Provides CRUD interface for app datasets (built-in) and user datasets (Profylr).
 * 
 * CHANGE HISTORY:
 * - 251201_0140 danielbeer: Created main dataset management tool component
 */

import React, { useState, useEffect } from 'react';
import { DatasetHeader } from './DatasetHeader';
import { CollapsibleDatasetDetails } from './CollapsibleDatasetDetails';
import { DataSourcesSection } from './DataSourcesSection';
import { DatasetForm } from './DatasetForm';
import type { Dataset, DatasetConfig, DataSourceReference } from '../types/DatasetTypes';
import './AggregatrDatasetTool.css';

export interface AggregatrDatasetToolProps {
  /** Application ID for scoping datasets */
  appId: string;
  
  /** Built-in app datasets (read-only) */
  appDatasets?: Dataset[];
  
  /** Callback when dataset is created */
  onDatasetCreate?: (dataset: DatasetConfig) => Promise<void>;
  
  /** Callback when dataset is updated */
  onDatasetUpdate?: (id: string, updates: Partial<Dataset>) => Promise<void>;
  
  /** Callback when dataset is deleted */
  onDatasetDelete?: (id: string) => Promise<void>;
  
  /** Callback when tool is closed */
  onClose?: () => void;
  
  /** Error handler */
  onError?: (error: string) => void;
}

export function AggregatrDatasetTool({
  appId,
  appDatasets = [],
  onDatasetCreate,
  onDatasetUpdate,
  onDatasetDelete,
  onClose,
  onError
}: AggregatrDatasetToolProps) {
  // State
  const [userDatasets, setUserDatasets] = useState<Dataset[]>([]);
  const [selectedDataset, setSelectedDataset] = useState<Dataset | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Load user datasets from Profylr on mount
  useEffect(() => {
    loadUserDatasets();
  }, [appId]);

  /**
   * Load user datasets from Profylr
   */
  const loadUserDatasets = async () => {
    try {
      setIsLoading(true);
      
      // TODO: Implement Profylr integration
      // const profylr = useProfile();
      // const datasets = await profylr.get(`aggregatr.${appId}.datasets`, []);
      
      // For now, use localStorage as fallback
      const storageKey = `opcentral.aggregatr.${appId}.datasets`;
      const stored = localStorage.getItem(storageKey);
      const datasets = stored ? JSON.parse(stored) : [];
      
      setUserDatasets(datasets);
    } catch (error) {
      console.error('Failed to load user datasets:', error);
      onError?.('Failed to load datasets');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Save user datasets to Profylr
   */
  const saveUserDatasets = async (datasets: Dataset[]) => {
    try {
      // TODO: Implement Profylr integration
      // const profylr = useProfile();
      // await profylr.set(`aggregatr.${appId}.datasets`, datasets);
      
      // For now, use localStorage as fallback
      const storageKey = `opcentral.aggregatr.${appId}.datasets`;
      localStorage.setItem(storageKey, JSON.stringify(datasets));
      
      setUserDatasets(datasets);
    } catch (error) {
      console.error('Failed to save user datasets:', error);
      onError?.('Failed to save datasets');
      throw error;
    }
  };

  /**
   * Handle dataset selection by ID
   */
  const handleSelectDataset = (datasetId: string) => {
    const dataset = allDatasets.find(d => d.id === datasetId);
    setSelectedDataset(dataset || null);
    setIsEditing(false);
    setIsCreating(false);
  };

  /**
   * Handle create new dataset
   */
  const handleCreate = () => {
    setSelectedDataset(null);
    setIsCreating(true);
    setIsEditing(true);
  };

  /**
   * Handle edit dataset
   */
  const handleEdit = (dataset: Dataset) => {
    setSelectedDataset(dataset);
    setIsEditing(true);
    setIsCreating(false);
  };

  /**
   * Handle save dataset
   */
  const handleSave = async (dataset: DatasetConfig) => {
    try {
      if (isCreating) {
        // Create new dataset
        const newDataset: Dataset = {
          ...dataset,
          id: `dataset-${Date.now()}`,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          isUserDataset: true
        };
        
        const updated = [...userDatasets, newDataset];
        await saveUserDatasets(updated);
        
        if (onDatasetCreate) {
          await onDatasetCreate(dataset);
        }
        
        setSelectedDataset(newDataset);
      } else if (selectedDataset) {
        // Update existing dataset
        const updated = userDatasets.map(d =>
          d.id === selectedDataset.id
            ? { ...d, ...dataset, updatedAt: new Date().toISOString() }
            : d
        );
        await saveUserDatasets(updated);
        
        if (onDatasetUpdate) {
          await onDatasetUpdate(selectedDataset.id, dataset);
        }
        
        setSelectedDataset({ ...selectedDataset, ...dataset });
      }
      
      setIsEditing(false);
      setIsCreating(false);
    } catch (error) {
      console.error('Failed to save dataset:', error);
      onError?.('Failed to save dataset');
    }
  };

  /**
   * Handle delete dataset
   */
  const handleDelete = async (dataset: Dataset) => {
    if (!dataset.isUserDataset) {
      onError?.('Cannot delete app datasets');
      return;
    }
    
    if (!confirm(`Delete dataset "${dataset.name}"? This cannot be undone.`)) {
      return;
    }
    
    try {
      const updated = userDatasets.filter(d => d.id !== dataset.id);
      await saveUserDatasets(updated);
      
      if (onDatasetDelete) {
        await onDatasetDelete(dataset.id);
      }
      
      if (selectedDataset?.id === dataset.id) {
        setSelectedDataset(null);
      }
    } catch (error) {
      console.error('Failed to delete dataset:', error);
      onError?.('Failed to delete dataset');
    }
  };

  /**
   * Handle cancel editing
   */
  const handleCancel = () => {
    setIsEditing(false);
    setIsCreating(false);
  };

  /**
   * Handle import dataset
   */
  const handleImport = async (datasetJson: string) => {
    try {
      const dataset = JSON.parse(datasetJson);
      
      // Validate dataset structure
      if (!dataset.name || !dataset.sources) {
        throw new Error('Invalid dataset format');
      }
      
      // Create as new user dataset
      const newDataset: Dataset = {
        ...dataset,
        id: `dataset-${Date.now()}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isUserDataset: true
      };
      
      const updated = [...userDatasets, newDataset];
      await saveUserDatasets(updated);
      
      setSelectedDataset(newDataset);
    } catch (error) {
      console.error('Failed to import dataset:', error);
      onError?.('Failed to import dataset. Please check the file format.');
    }
  };

  /**
   * Handle export dataset
   */
  const handleExport = (dataset: Dataset) => {
    try {
      const dataStr = JSON.stringify(dataset, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${dataset.name.replace(/\s+/g, '-').toLowerCase()}-dataset.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export dataset:', error);
      onError?.('Failed to export dataset');
    }
  };

  // Combine app and user datasets
  const allDatasets = [...appDatasets, ...userDatasets];
  
  /**
   * Handle add data source
   */
  const handleAddSource = (sourceRef: DataSourceReference) => {
    if (!selectedDataset) return;
    
    const updated: Dataset = {
      ...selectedDataset,
      sources: [...selectedDataset.sources, sourceRef],
      updatedAt: new Date().toISOString()
    };
    
    handleSave(updated);
  };
  
  /**
   * Handle remove data source
   */
  const handleRemoveSource = (sourceRef: DataSourceReference) => {
    if (!selectedDataset) return;
    
    const updated: Dataset = {
      ...selectedDataset,
      sources: selectedDataset.sources.filter(s => {
        if ('interactrSourceId' in s && 'interactrSourceId' in sourceRef) {
          return s.interactrSourceId !== sourceRef.interactrSourceId;
        }
        if ('profylrCollection' in s && 'profylrCollection' in sourceRef) {
          return s.profylrCollection !== sourceRef.profylrCollection;
        }
        if ('fylrPath' in s && 'fylrPath' in sourceRef) {
          return s.fylrPath !== sourceRef.fylrPath;
        }
        return true;
      }),
      updatedAt: new Date().toISOString()
    };
    
    handleSave(updated);
  };

  // Render content
  const content = (
    <div className={`aggregatr-dataset-tool ${isExpanded ? 'expanded' : 'compact'}`}>
      {/* Close button (if provided) */}
      {onClose && !isExpanded && (
        <button 
          className="close-button" 
          onClick={onClose}
          title="Close"
        >
          ✕
        </button>
      )}
      
      {/* Loading State */}
      {isLoading ? (
        <div className="loading-state">
          <div className="spinner">⟳</div>
          <p>Loading datasets...</p>
        </div>
      ) : (
        <>
          {/* Dataset Header - Compact Selector */}
          <DatasetHeader
            datasets={allDatasets}
            selectedDataset={selectedDataset}
            onSelectDataset={handleSelectDataset}
            onCreateDataset={handleCreate}
            onImportDataset={handleImport}
            onExpand={() => setIsExpanded(!isExpanded)}
            isExpanded={isExpanded}
          />
          
          {/* Dataset Details - Collapsible */}
          {selectedDataset && !isEditing && !isCreating && (
            <CollapsibleDatasetDetails
              dataset={selectedDataset}
              onEdit={() => handleEdit(selectedDataset)}
              onExport={() => handleExport(selectedDataset)}
              onDelete={() => handleDelete(selectedDataset)}
            />
          )}
          
          {/* Editing/Creating Form */}
          {(isEditing || isCreating) && (
            <DatasetForm
              dataset={selectedDataset || undefined}
              onSave={handleSave}
              onCancel={handleCancel}
              isCreating={isCreating}
            />
          )}
          
          {/* Data Sources Section */}
          {selectedDataset && !isEditing && !isCreating && (
            <DataSourcesSection
              dataset={selectedDataset}
              onAddSource={handleAddSource}
              onRemoveSource={handleRemoveSource}
            />
          )}
          
          {/* No Dataset Selected */}
          {!selectedDataset && !isCreating && (
            <div className="no-dataset-selected">
              <div className="empty-icon">📊</div>
              <h3>No Dataset Selected</h3>
              <p>Select a dataset from the dropdown or create a new one</p>
            </div>
          )}
        </>
      )}
    </div>
  );
  
  // Wrap in modal if expanded
  if (isExpanded) {
    return (
      <div className="dataset-tool-modal-overlay" onClick={() => setIsExpanded(false)}>
        <div className="dataset-tool-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Dataset Manager - Expanded View</h2>
            <div className="modal-actions">
              <button onClick={() => setIsExpanded(false)}>⤓ Collapse</button>
              {onClose && (
                <button onClick={onClose}>✕ Close</button>
              )}
            </div>
          </div>
          {content}
        </div>
      </div>
    );
  }
  
  return content;
}
